import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Divider } from 'src/components/Divider';
import { Dot } from 'src/components/Dot';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { formatTime } from 'src/utils/dateFormatter';

interface CalendarPopoverContentProps {
  event: any;
}

export const CalendarPopoverContent: React.FC<CalendarPopoverContentProps> = ({
  event,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'schedules.calendar.popover' });

  return (
    <>
      <Box>
        <Box sx={{ p: 1, pr: 2 }}>
          <Typography variant="h5" gutterBottom>
            {event?.title}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            <Dot sx={{ mr: 0.5, bgcolor: event?.textColor }} />{' '}
            {formatTime(event?.startStr)} - {formatTime(event?.endStr)}
          </Typography>
        </Box>
        <Divider sx={{ my: 0.5, mx: 1 }} />
        <Stack sx={{ p: 1, pr: 2 }} spacing={1}>
          {(event.extendedProps.objectType === 'appointment' || event.extendedProps.objectType === 'scheduled_appointment') && (
            <>
              <PopoverField label={t('trainer')}>{event?.extendedProps?.data.primaryTrainerName}</PopoverField>
              {event?.extendedProps?.data.location && (
                <PopoverField label={t('location')}>{event?.extendedProps?.data.location}</PopoverField>
              )}
              <PopoverField label={t('service')}>{event?.extendedProps?.data.service}</PopoverField>
              {event?.extendedProps?.data.resourceNames && (
                <PopoverField label={t('assets')}>{event?.extendedProps?.data.resourceNames}</PopoverField>
              )}
              <PopoverField label={t('participants')}>{event?.extendedProps?.data.clientsCount}</PopoverField>
              {event?.extendedProps?.data.visitsRemaining > 0 && (
                <PopoverField label={t('spotsRemaining')}>{event?.extendedProps?.data.visitsRemaining}</PopoverField>
              )}
              {event?.extendedProps?.data.waitlistCount > 0 && (
                <PopoverField label={t('waitlist')}>{event?.extendedProps?.data.waitlistCount}</PopoverField>
              )}
              {event?.extendedProps?.data.scheduleId && (
                <Typography variant="body2" color="text.secondary">
                  {t('createdFromSchedule')}
                </Typography>
              )}
              {event?.extendedProps?.data.notes && (
                <PopoverField label={t('notes')}>{event?.extendedProps?.data.notes}</PopoverField>
              )}
            </>
          )}
          {event.extendedProps.objectType === 'schedule' && (
            <>
              <PopoverField label={t('trainer')}>{event?.extendedProps?.data.trainerName}</PopoverField>
              {event?.extendedProps?.data.location && (
                <PopoverField label={t('location')}>{event?.extendedProps?.data.location}</PopoverField>
              )}
            </>
          )}
        </Stack>
      </Box>
    </>
  );
};

const PopoverField = ({ label, children }: { label: string; children: ReactNode }) => (
  <Typography variant="body2">
    <Typography variant="body2" component="span" color="text.secondary">
      {label}:
    </Typography>
    {' '}{children}
  </Typography>
);
